import type { PayorOverviewForm } from '../components/payor/payorConfigTypes'
import {
  normalizeNextStepTabId,
  type PayorConfigTabId,
} from '../components/payor/payorConfigTypes'
import type {
  Payor,
  PayorCappedRentalsForm,
  PayorFeeScheduleRow,
  PayorOwnershipTransferForm,
  PayorPolicyRulesForm,
  PayorPriorAuthForm,
  PayorStatus,
  Plan,
} from '../data/payorData'
import { apiJsonRequest, apiRequest } from './apiClient'

const CONFIG_PATH = '/payors/configuration'

export type PayorConfigurationSummary = {
  id: string
  payorName: string
  payorId: string
  contractType: string
  effectiveDate: string
  planCount: number
  status: PayorStatus | 'draft'
  wizardStep: number
  configCompletedAt: string | null
  createdAt: string
}

export type PayorConfigurationFull = Payor & {
  wizardStep: number
  configCompletedAt: string | null
}

export type WizardStepResult = {
  payorId: string
  wizardStep: number
  nextStep: PayorConfigTabId | null
}

export type PayorConfigurationListResult = {
  items: PayorConfigurationSummary[]
  meta: {
    total: number
    page: number
    limit: number
    pages: number
  }
}

export type ApiPayorPlan = {
  id?: string
  plan_name?: string
  planName?: string
  plan_type?: string
  planType?: string
  auto_check_frequency?: string
  autoCheckFrequency?: string
  status?: string
}

export type ApiPayor = {
  id?: string
  name?: string
  payor_name?: string
  payorName?: string
  status?: string
  plans?: ApiPayorPlan[]
}

type ApiRow = Record<string, unknown>

function asRecord(value: unknown): ApiRow | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as ApiRow) : null
}

function readString(row: ApiRow, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === 'string') return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

function readNumber(row: ApiRow, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) return parsed
    }
  }
  return undefined
}

function readBoolean(row: ApiRow, ...keys: string[]): boolean | undefined {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true
      if (normalized === 'false' || normalized === '0' || normalized === 'no') return false
    }
  }
  return undefined
}

function readStringArray(row: ApiRow, ...keys: string[]): string[] | undefined {
  for (const key of keys) {
    const value = row[key]
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string')
    }
  }
  return undefined
}

function unwrapData(body: unknown): ApiRow | null {
  const record = asRecord(body)
  if (!record) return null
  if (record.data !== undefined) {
    const nested = asRecord(record.data)
    if (nested) return nested
  }
  return record
}

function normalizePayorStatus(value: unknown): PayorStatus | 'draft' {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (raw === 'draft') return 'draft'
  if (raw === 'pending') return 'pending'
  if (raw === 'inactive') return 'inactive'
  return 'active'
}

function toPayorStatus(value: PayorStatus | 'draft'): PayorStatus {
  return value === 'draft' ? 'pending' : value
}

function normalizePlan(value: unknown): Plan | null {
  const row = asRecord(value)
  if (!row) return null
  const planName = readString(row, 'planName', 'plan_name')
  if (!planName) return null

  return {
    id: readString(row, 'id') || `plan-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    planName,
    planId: readString(row, 'planId', 'plan_id') || undefined,
    planType: readString(row, 'planType', 'plan_type'),
    groupNumber: readString(row, 'groupNumber', 'group_number') || undefined,
    network: readString(row, 'network') || undefined,
    autoCheckFrequency: readString(row, 'autoCheckFrequency', 'auto_check_frequency'),
    status: readString(row, 'status') || 'active',
    createdAt:
      readString(row, 'createdAt', 'created_at') || new Date().toISOString().slice(0, 10),
  }
}

function normalizePolicyRules(value: unknown): PayorPolicyRulesForm | undefined {
  const row = asRecord(value)
  if (!row) return undefined
  return {
    timelyFilingLimit: readString(row, 'timelyFilingLimit', 'timely_filing_limit'),
    correctedClaimDeadline: readString(row, 'correctedClaimDeadline', 'corrected_claim_deadline'),
    coordinationOfBenefits: readString(row, 'coordinationOfBenefits', 'coordination_of_benefits'),
    assignmentOfBenefits: readString(row, 'assignmentOfBenefits', 'assignment_of_benefits'),
    claimFrequencyType: readString(row, 'claimFrequencyType', 'claim_frequency_type'),
    renderingProviderRequired: readString(
      row,
      'renderingProviderRequired',
      'rendering_provider_required'
    ),
    requireMemberId: readBoolean(row, 'requireMemberId', 'require_member_id') ?? true,
    acceptCrossoverClaims: readBoolean(row, 'acceptCrossoverClaims', 'accept_crossover_claims') ?? false,
    allowBundledBilling: readBoolean(row, 'allowBundledBilling', 'allow_bundled_billing') ?? true,
    sendClaimStatusInquiry:
      readBoolean(row, 'sendClaimStatusInquiry', 'send_claim_status_inquiry') ?? true,
  }
}

function normalizeFeeScheduleRow(value: unknown): PayorFeeScheduleRow | null {
  const row = asRecord(value)
  if (!row) return null
  const hcpcsCode = readString(row, 'hcpcsCode', 'hcpcs_code')
  if (!hcpcsCode) return null

  const allowedRaw = row.allowedAmount ?? row.allowed_amount
  const allowedAmount =
    typeof allowedRaw === 'number'
      ? `$${allowedRaw.toFixed(2)}`
      : readString(row, 'allowedAmount', 'allowed_amount')

  const percentRaw = row.percentOfMedicare ?? row.percent_of_medicare
  const percentOfMedicare =
    typeof percentRaw === 'number'
      ? `${percentRaw}%`
      : readString(row, 'percentOfMedicare', 'percent_of_medicare') || '100%'

  return {
    id: readString(row, 'id') || `fs-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    hcpcsCode,
    description: readString(row, 'description'),
    modifier: readString(row, 'modifier'),
    allowedAmount,
    feeBasis: readString(row, 'feeBasis', 'fee_basis') || 'Per month',
    percentOfMedicare,
    effectiveDate: readString(row, 'effectiveDate', 'effective_date'),
    isDraft: readBoolean(row, 'isDraft', 'is_draft') ?? false,
  }
}

function normalizeCappedRentals(value: unknown): PayorCappedRentalsForm | undefined {
  const row = asRecord(value)
  if (!row) return undefined
  return {
    rentalCapPeriod: readString(row, 'rentalCapPeriod', 'rental_cap_period'),
    capResetsOn: readString(row, 'capResetsOn', 'cap_resets_on'),
    months1To3Rate: readString(row, 'months1To3Rate', 'months_1_to_3_rate'),
    months4To13Rate: readString(row, 'months4To13Rate', 'months_4_to_13_rate'),
    postCapMsRate: readString(row, 'postCapMsRate', 'post_cap_ms_rate'),
    equipmentCategories:
      readStringArray(row, 'equipmentCategories', 'equipment_categories') ?? [],
    notifyProviderAtMonth10:
      readBoolean(row, 'notifyProviderAtMonth10', 'notify_provider_at_month_10') ?? true,
    autoConvertToPurchaseAtCap:
      readBoolean(row, 'autoConvertToPurchaseAtCap', 'auto_convert_to_purchase_at_cap') ?? false,
    maintenanceServicingPostCap:
      readBoolean(row, 'maintenanceServicingPostCap', 'maintenance_servicing_post_cap') ?? true,
    pauseBillingDuringHospitalStay:
      readBoolean(row, 'pauseBillingDuringHospitalStay', 'pause_billing_during_hospital_stay') ??
      true,
  }
}

function normalizeOwnershipTransfer(value: unknown): PayorOwnershipTransferForm | undefined {
  const row = asRecord(value)
  if (!row) return undefined
  return {
    transferTrigger: readString(row, 'transferTrigger', 'transfer_trigger'),
    ownershipTransferModifier: readString(
      row,
      'ownershipTransferModifier',
      'ownership_transfer_modifier'
    ),
    lumpSumPurchaseAmount: readString(row, 'lumpSumPurchaseAmount', 'lump_sum_purchase_amount'),
    documentationRequired: readString(row, 'documentationRequired', 'documentation_required'),
    requirePayorPreApproval:
      readBoolean(row, 'requirePayorPreApproval', 'require_payor_pre_approval') ?? true,
    allowPatientBuyoutBeforeCap:
      readBoolean(row, 'allowPatientBuyoutBeforeCap', 'allow_patient_buyout_before_cap') ?? false,
    sendPatientOwnershipLetter:
      readBoolean(row, 'sendPatientOwnershipLetter', 'send_patient_ownership_letter') ?? true,
  }
}

function normalizePriorAuth(value: unknown): PayorPriorAuthForm | undefined {
  const row = asRecord(value)
  if (!row) return undefined

  const checklistRaw = row.clinicalDocChecklist ?? row.clinical_doc_checklist
  const clinicalDocChecklist = Array.isArray(checklistRaw)
    ? checklistRaw
        .map((item) => {
          const entry = asRecord(item)
          if (!entry) return null
          const id = readString(entry, 'id')
          if (!id) return null
          return {
            id,
            title: readString(entry, 'title') || id,
            description: readString(entry, 'description'),
            checked: readBoolean(entry, 'checked') ?? false,
          }
        })
        .filter(Boolean)
    : []

  return {
    paSubmissionMethod: readString(row, 'paSubmissionMethod', 'pa_submission_method'),
    standardTurnaroundTime: readString(row, 'standardTurnaroundTime', 'standard_turnaround_time'),
    paPortalUrl: readString(row, 'paPortalUrl', 'pa_portal_url'),
    paFaxNumber: readString(row, 'paFaxNumber', 'pa_fax_number'),
    clinicalDocChecklist: clinicalDocChecklist as PayorPriorAuthForm['clinicalDocChecklist'],
  }
}

export function normalizePayorConfigurationSummary(value: unknown): PayorConfigurationSummary | null {
  const row = asRecord(value)
  if (!row) return null

  const id = readString(row, 'id')
  const payorName = readString(row, 'payorName', 'payor_name', 'name')
  if (!id || !payorName) return null

  return {
    id,
    payorName,
    payorId: readString(row, 'payorId', 'payor_id'),
    contractType: readString(row, 'contractType', 'contract_type'),
    effectiveDate: readString(row, 'effectiveDate', 'effective_date'),
    planCount: readNumber(row, 'planCount', 'plan_count') ?? 0,
    status: normalizePayorStatus(row.status),
    wizardStep: readNumber(row, 'wizardStep', 'wizard_step') ?? 1,
    configCompletedAt:
      readString(row, 'configCompletedAt', 'config_completed_at') || null,
    createdAt: readString(row, 'createdAt', 'created_at') || new Date().toISOString(),
  }
}

export function normalizePayorConfigurationFull(value: unknown): PayorConfigurationFull | null {
  const row = unwrapData(value) ?? asRecord(value)
  if (!row) return null

  const overview = asRecord(row.overview)
  const payorName =
    readString(overview ?? {}, 'payorName', 'payor_name') ||
    readString(row, 'payorName', 'payor_name', 'name')
  if (!payorName) return null

  const id = readString(row, 'id')
  if (!id) return null

  const plansRaw = row.plans
  const plans = Array.isArray(plansRaw)
    ? (plansRaw.map(normalizePlan).filter(Boolean) as Plan[])
    : []

  const feeSchedulesRaw = row.feeSchedules ?? row.fee_schedules ?? row.lines
  const feeSchedules = Array.isArray(feeSchedulesRaw)
    ? (feeSchedulesRaw.map(normalizeFeeScheduleRow).filter(Boolean) as PayorFeeScheduleRow[])
    : []

  const status = normalizePayorStatus(row.status ?? overview?.status)
  const activePayor = readBoolean(overview ?? {}, 'activePayor', 'active_payor')

  return {
    id,
    payorName,
    payorId: readString(overview ?? {}, 'payorId', 'payor_id') || readString(row, 'payorId', 'payor_id'),
    contractType: readString(overview ?? {}, 'contractType', 'contract_type'),
    effectiveDate: readString(overview ?? {}, 'effectiveDate', 'effective_date'),
    terminationDate: readString(overview ?? {}, 'terminationDate', 'termination_date') || undefined,
    status: toPayorStatus(status),
    plans,
    createdAt: readString(row, 'createdAt', 'created_at') || new Date().toISOString().slice(0, 10),
    npiTaxId: readString(overview ?? {}, 'npiTaxId', 'npi_tax_id') || undefined,
    contactName: readString(overview ?? {}, 'contactName', 'contact_name') || undefined,
    contactPhone: readString(overview ?? {}, 'contactPhone', 'contact_phone') || undefined,
    claimsMailingAddress:
      readString(overview ?? {}, 'claimsMailingAddress', 'claims_mailing_address') || undefined,
    billingState: readString(overview ?? {}, 'billingState', 'billing_state') || undefined,
    submissionMethod:
      readString(overview ?? {}, 'submissionMethod', 'submission_method') || undefined,
    providerPortalUrl:
      readString(overview ?? {}, 'providerPortalUrl', 'provider_portal_url') || undefined,
    ediSubmitterId: readString(overview ?? {}, 'ediSubmitterId', 'edi_submitter_id') || undefined,
    activePayor: activePayor ?? status !== 'inactive',
    electronicRemittance:
      readBoolean(overview ?? {}, 'electronicRemittance', 'electronic_remittance') ?? true,
    realTimeEligibility:
      readBoolean(overview ?? {}, 'realTimeEligibility', 'real_time_eligibility') ?? false,
    autoPostEraPayments:
      readBoolean(overview ?? {}, 'autoPostEraPayments', 'auto_post_era_payments') ?? true,
    contractNotes: readString(overview ?? {}, 'contractNotes', 'contract_notes') || undefined,
    policyRules: normalizePolicyRules(row.policyRules ?? row.policy_rules),
    feeSchedules: feeSchedules.length > 0 ? feeSchedules : undefined,
    cappedRentals: normalizeCappedRentals(row.cappedRentals ?? row.capped_rentals),
    ownershipTransfer: normalizeOwnershipTransfer(
      row.ownershipTransfer ?? row.ownership_transfer
    ),
    priorAuth: normalizePriorAuth(row.priorAuth ?? row.prior_auth),
    wizardStep: readNumber(row, 'wizardStep', 'wizard_step') ?? 1,
    configCompletedAt: readString(row, 'configCompletedAt', 'config_completed_at') || null,
  }
}

function normalizeWizardStepResult(body: unknown): WizardStepResult {
  const row = unwrapData(body) ?? asRecord(body)
  if (!row) {
    throw new Error('Wizard step response format is invalid.')
  }

  const payorId = readString(row, 'payorId', 'payor_id', 'id')
  if (!payorId) {
    throw new Error('Wizard step response is missing payorId.')
  }

  const wizardStep = readNumber(row, 'wizardStep', 'wizard_step') ?? 1
  const nextStepRaw = row.nextStep ?? row.next_step
  const nextStep =
    normalizeNextStepTabId(typeof nextStepRaw === 'string' ? nextStepRaw : '') ??
    null

  return { payorId, wizardStep, nextStep }
}

function parseMoney(value: string): number | null {
  const cleaned = value.replace(/[^0-9.-]/g, '')
  if (!cleaned) return null
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

function parsePercent(value: string): number | null {
  const cleaned = value.replace(/[^0-9.-]/g, '')
  if (!cleaned) return null
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

function overviewStepPayload(payorId: string, overview: PayorOverviewForm): ApiRow {
  return {
    payorId,
    payorName: overview.payorName.trim(),
    npiTaxId: overview.npiTaxId.trim(),
    contractType: overview.contractType,
    effectiveDate: overview.effectiveDate,
    terminationDate: overview.terminationDate,
    contactName: overview.contactName.trim(),
    contactPhone: overview.contactPhone.trim(),
    claimsMailingAddress: overview.claimsMailingAddress.trim(),
    billingState: overview.billingState,
    submissionMethod: overview.submissionMethod,
    providerPortalUrl: overview.providerPortalUrl.trim(),
    ediSubmitterId: overview.ediSubmitterId.trim(),
    activePayor: overview.activePayor,
    electronicRemittance: overview.electronicRemittance,
    realTimeEligibility: overview.realTimeEligibility,
    autoPostEraPayments: overview.autoPostEraPayments,
    contractNotes: overview.contractNotes.trim(),
  }
}

function plansStepPayload(
  payorId: string,
  plans: Plan[],
  policyRules: PayorPolicyRulesForm
): ApiRow {
  return {
    payorId,
    plans: plans
      .filter((plan) => plan.planName.trim())
      .map((plan) => ({
        ...(plan.id && !plan.id.startsWith('plan-') ? { id: plan.id } : {}),
        planName: plan.planName.trim(),
        planId: plan.planId?.trim() ?? '',
        planType: plan.planType,
        groupNumber: plan.groupNumber?.trim() ?? '',
        network: plan.network?.trim() ?? '',
        autoCheckFrequency: plan.autoCheckFrequency,
        status: plan.status === 'draft' ? 'active' : plan.status,
      })),
    policyRules,
  }
}

function feeSchedulesStepPayload(
  payorId: string,
  scheduleYear: string,
  scheduleLabel: string,
  rows: PayorFeeScheduleRow[]
): ApiRow {
  return {
    payorId,
    scheduleYear,
    scheduleLabel,
    lines: rows
      .filter((row) => row.hcpcsCode.trim())
      .map((row) => {
        const allowedAmount = parseMoney(row.allowedAmount)
        const percentOfMedicare = parsePercent(row.percentOfMedicare)
        return {
          ...(row.id && !row.id.startsWith('fs-') ? { id: row.id } : {}),
          hcpcsCode: row.hcpcsCode.trim(),
          description: row.description.trim(),
          modifier: row.modifier.trim(),
          allowedAmount: allowedAmount ?? row.allowedAmount,
          feeBasis: row.feeBasis,
          percentOfMedicare: percentOfMedicare ?? row.percentOfMedicare,
          effectiveDate: row.effectiveDate,
          isDraft: row.isDraft ?? false,
        }
      }),
  }
}

function priorAuthStepPayload(payorId: string, priorAuth: PayorPriorAuthForm): ApiRow {
  return {
    payorId,
    paSubmissionMethod: priorAuth.paSubmissionMethod,
    standardTurnaroundTime: priorAuth.standardTurnaroundTime,
    paPortalUrl: priorAuth.paPortalUrl.trim(),
    paFaxNumber: priorAuth.paFaxNumber.trim(),
    clinicalDocChecklist: priorAuth.clinicalDocChecklist.map((item) => ({
      id: item.id,
      checked: item.checked,
    })),
  }
}

type ConfigurationStepSlug =
  | 'overview'
  | 'plans-policies'
  | 'fee-schedules'
  | 'capped-rentals'
  | 'ownership-transfer'
  | 'prior-auth'

function configurationStepPath(payorId: string, step: ConfigurationStepSlug): string {
  return `/payors/${encodeURIComponent(payorId)}/configuration/steps/${step}`
}

async function postOverviewStep(payorId: string, payload: ApiRow): Promise<WizardStepResult> {
  const response = await apiJsonRequest<unknown>(`${CONFIG_PATH}/steps/overview`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return normalizeWizardStepResult(response)
}

async function putOverviewStep(payorId: string, payload: ApiRow): Promise<WizardStepResult> {
  const response = await apiJsonRequest<unknown>(
    configurationStepPath(payorId, 'overview'),
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    }
  )
  return normalizeWizardStepResult(response)
}

async function putConfigurationStep(
  payorId: string,
  step: Exclude<ConfigurationStepSlug, 'overview'>,
  payload: ApiRow
): Promise<WizardStepResult> {
  const response = await apiJsonRequest<unknown>(configurationStepPath(payorId, step), {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  return normalizeWizardStepResult(response)
}

export type CreatePayorPayload = {
  name: string
  status: string
}

/** POST /payors — creates the base payor record before configuration wizard steps. */
export async function createPayor(payload: CreatePayorPayload): Promise<string> {
  const response = await apiJsonRequest<unknown>('/payors', {
    method: 'POST',
    body: JSON.stringify({
      name: payload.name.trim(),
      status: payload.status.trim() || 'active',
    }),
  })
  const row = unwrapData(response) ?? asRecord(response)
  if (!row) {
    throw new Error('Create payor response format is invalid.')
  }
  const id = readString(row, 'id')
  if (!id) {
    throw new Error('Create payor response is missing id.')
  }
  return id
}

/** GET /payors/configuration */
export async function listPayorConfigurationSummaries(opts?: {
  q?: string
  page?: number
  limit?: number
}): Promise<PayorConfigurationListResult> {
  const params = new URLSearchParams()
  if (opts?.q?.trim()) params.set('q', opts.q.trim())
  if (opts?.page != null) params.set('page', String(opts.page))
  if (opts?.limit != null) params.set('limit', String(opts.limit))
  const query = params.toString()
  const path = query ? `${CONFIG_PATH}?${query}` : CONFIG_PATH

  const response = await apiJsonRequest<unknown>(path, { method: 'GET' })
  const record = unwrapData(response) ?? asRecord(response) ?? {}

  const itemsRaw = record.items ?? record.data
  const items = Array.isArray(itemsRaw)
    ? (itemsRaw.map(normalizePayorConfigurationSummary).filter(Boolean) as PayorConfigurationSummary[])
    : []

  const metaRecord = asRecord(record.meta) ?? {}
  return {
    items,
    meta: {
      total: readNumber(metaRecord, 'total') ?? items.length,
      page: readNumber(metaRecord, 'page') ?? 1,
      limit: readNumber(metaRecord, 'limit') ?? items.length,
      pages: readNumber(metaRecord, 'pages') ?? 1,
    },
  }
}

/** GET /payors/:payorId/configuration */
export async function getPayorConfiguration(payorId: string): Promise<PayorConfigurationFull> {
  const response = await apiJsonRequest<unknown>(
    `/payors/${encodeURIComponent(payorId)}/configuration`,
    { method: 'GET' }
  )
  const full = normalizePayorConfigurationFull(response)
  if (!full) {
    throw new Error('Payor configuration response format is invalid.')
  }
  return full
}

export async function savePayorConfigurationOverviewStep(
  payorId: string,
  overview: PayorOverviewForm,
  options?: { isUpdate?: boolean }
): Promise<WizardStepResult> {
  const payload = overviewStepPayload(payorId, overview)
  if (options?.isUpdate) {
    return putOverviewStep(payorId, payload)
  }
  return postOverviewStep(payorId, payload)
}

export async function savePayorConfigurationPlansStep(
  payorId: string,
  plans: Plan[],
  policyRules: PayorPolicyRulesForm
): Promise<WizardStepResult> {
  return putConfigurationStep(
    payorId,
    'plans-policies',
    plansStepPayload(payorId, plans, policyRules)
  )
}

export async function savePayorConfigurationFeeSchedulesStep(
  payorId: string,
  scheduleYear: string,
  scheduleLabel: string,
  rows: PayorFeeScheduleRow[]
): Promise<WizardStepResult> {
  return putConfigurationStep(
    payorId,
    'fee-schedules',
    feeSchedulesStepPayload(payorId, scheduleYear, scheduleLabel, rows)
  )
}

export async function savePayorConfigurationCappedRentalsStep(
  payorId: string,
  cappedRentals: PayorCappedRentalsForm
): Promise<WizardStepResult> {
  return putConfigurationStep(payorId, 'capped-rentals', {
    payorId,
    ...cappedRentals,
  })
}

export async function savePayorConfigurationOwnershipTransferStep(
  payorId: string,
  ownershipTransfer: PayorOwnershipTransferForm
): Promise<WizardStepResult> {
  return putConfigurationStep(payorId, 'ownership-transfer', {
    payorId,
    ...ownershipTransfer,
  })
}

export async function savePayorConfigurationPriorAuthStep(
  payorId: string,
  priorAuth: PayorPriorAuthForm
): Promise<WizardStepResult> {
  return putConfigurationStep(
    payorId,
    'prior-auth',
    priorAuthStepPayload(payorId, priorAuth)
  )
}

/** POST /payors/:payorId/configuration/finalize */
export async function finalizePayorConfiguration(payorId: string): Promise<void> {
  await apiJsonRequest<unknown>(
    `/payors/${encodeURIComponent(payorId)}/configuration/finalize`,
    { method: 'POST' }
  )
}

/** POST /payors/:payorId/plans/upload */
export async function uploadPayorPlansExcel(payorId: string, file: File): Promise<Plan[]> {
  const form = new FormData()
  form.append('file', file)
  const response = await apiRequest(`/payors/${encodeURIComponent(payorId)}/plans/upload`, {
    method: 'POST',
    body: form,
  })
  const text = await response.text()
  if (!response.ok) {
    throw new Error(text || `Plan upload failed (${response.status}).`)
  }

  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      throw new Error('Plan upload returned invalid JSON.')
    }
  }

  const record = unwrapData(body) ?? asRecord(body)
  const plansRaw = record?.plans ?? record?.items ?? record?.data
  if (!Array.isArray(plansRaw)) {
    return []
  }
  return plansRaw.map(normalizePlan).filter(Boolean) as Plan[]
}

function parseDeleteErrorMessage(text: string, fallback: string): string {
  if (!text) return fallback
  try {
    const parsed = JSON.parse(text) as { message?: string; msg?: string }
    return parsed?.message || parsed?.msg || fallback
  } catch {
    return fallback
  }
}

/** DELETE /payors/:payorId/configuration */
export async function deletePayorConfiguration(payorId: string): Promise<void> {
  const response = await apiRequest(
    `/payors/${encodeURIComponent(payorId)}/configuration`,
    {
      method: 'DELETE',
    }
  )
  if (!response.ok) {
    const text = await response.text()
    throw new Error(
      parseDeleteErrorMessage(text, `Failed to delete payor configuration (${response.status}).`)
    )
  }
}

export function resolvePayorDisplayName(payor: ApiPayor): string {
  return (
    (typeof payor.name === 'string' && payor.name.trim()) ||
    (typeof payor.payor_name === 'string' && payor.payor_name.trim()) ||
    (typeof payor.payorName === 'string' && payor.payorName.trim()) ||
    'Unnamed payor'
  )
}

export function resolvePayorApiId(payor: ApiPayor): string {
  return typeof payor.id === 'string' ? payor.id.trim() : ''
}

export function matchPayorForPrimaryPayorName(
  primaryPayor: string,
  payors: ApiPayor[]
): ApiPayor | undefined {
  const needle = primaryPayor.trim().toLowerCase()
  if (!needle || needle === 'unknown payor') return undefined

  const exact = payors.find((payor) => resolvePayorDisplayName(payor).toLowerCase() === needle)
  if (exact) return exact

  return payors.find((payor) => {
    const name = resolvePayorDisplayName(payor).toLowerCase()
    return name.includes(needle) || needle.includes(name)
  })
}

export function payorConfigurationToApiPayor(summary: PayorConfigurationSummary): ApiPayor {
  return {
    id: summary.id,
    name: summary.payorName,
    payor_name: summary.payorName,
    status: summary.status === 'draft' ? 'pending' : summary.status,
  }
}

/** Prior-auth dropdown — completed/active payor configurations only. */
export async function listPayors(): Promise<ApiPayor[]> {
  const { items } = await listPayorConfigurationSummaries()
  return items
    .filter((item) => item.status === 'active' || item.configCompletedAt)
    .map(payorConfigurationToApiPayor)
}

/** @deprecated Use listPayorConfigurationSummaries */
export async function listPayorConfigurations(q?: string): Promise<Payor[]> {
  const { items } = await listPayorConfigurationSummaries({ q })
  return items.map((item) => ({
    id: item.id,
    payorName: item.payorName,
    payorId: item.payorId,
    contractType: item.contractType,
    effectiveDate: item.effectiveDate,
    status: toPayorStatus(item.status),
    plans: Array.from({ length: item.planCount }, (_, index) => ({
      id: `plan-placeholder-${index}`,
      planName: '',
      planType: '',
      autoCheckFrequency: '',
      status: 'active',
      createdAt: item.createdAt,
    })),
    createdAt: item.createdAt.slice(0, 10),
  }))
}
